import { useEffect, useState } from 'react';
import { GridRowModes, useGridApiRef } from '@mui/x-data-grid-premium';
import { Box } from '@mui/material';
import dayjs from 'dayjs';

import { sxConfig } from './ProductionItemProposalTable.style';
import { cleanValues, dataGridPremiumColumns } from '../../utils/helper';
import { DataGridIdentifiers, InputUI, DatagridPremiumUi } from '../Interface';
import useConfig from '../../context/ConfigContext';
import useUsers from '../../context/UsersContext';
import productionItemProposalService from '../../service/productionItemProposal.service';
const ProductionItemProposalTable = () => {
  const apiRef = useGridApiRef();
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();

  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const [refetchTable, setRefetchTable] = useState(false);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
  }, []);

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'dueDate', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 100,
        page: 0,
      },
    },
    density: 'compact',
  };

  const getList = (gridState) => {
    productionItemProposalService.getData(gridState).then((res) => {
      setReports(res);
    });
  };

  const handleFieldChange = async (fieldName, newValue, row) => {
    try {
      const requestBody = {
        productionItemId: row.productionItemId,
        fromStock: parseFloat(row.fromStock) || null,
        toBeOrdered: parseFloat(row.toBeOrdered) || null,
        supplierOrderNum: row.supplierOrderNum,
        updatedBy: user.userId,
      };

      await productionItemProposalService.upsert(cleanValues(requestBody));
      setRefetchTable(true);
    } catch (error) {
      console.error('Error updating field:', error);
    }
  };

  const columns = () => {
    return dataGridPremiumColumns([
      {
        field: 'workingNumber',
        headerName: 'Gyártás szám',
        type: 'string',
      },
      {
        field: 'reference',
        headerName: 'Hivatkozási szám',
        type: 'string',
      },
      {
        field: 'companyName',
        headerName: 'Partner',
        type: 'string',
      },
      {
        field: 'dueDate',
        headerName: 'Határidő',
        type: 'date',
        valueGetter: (_data, row) => {
          return row.dueDate ? new Date(row.dueDate) : null;
        },
        renderCell: (data) => {
          return dayjs(data.row.dueDate).format('YYYY.MM.DD');
        },
      },
      {
        field: 'itemCode',
        headerName: 'Alapanyag cikkszám',
        type: 'string',
      },

      {
        field: 'itemName',
        headerName: 'Alapanyag cikknév',
        type: 'string',
      },

      {
        field: 'prodItemQty',
        headerName: 'Mennyiség',
        type: 'number',
        sortable: false,
      },

      {
        field: 'quantityUnit',
        headerName: 'Mennyiségi egység',
        type: 'string',
      },

      {
        field: 'articleNumber',
        headerName: 'Késztermék cikkszám',
        type: 'string',
      },

      {
        field: 'articleName',
        headerName: 'Késztermék cikknév',
        type: 'string',
      },
      {
        field: 'joker_date1',
        headerName: 'Logisztikai határidő',
        type: 'date',
        valueGetter: (_data, row) => {
          return row.joker_date1 ? new Date(row.joker_date1) : null;
        },
        renderCell: (data) => {
          return data.row.joker_date1 ? dayjs(data.row.joker_date1).format('YYYY.MM.DD') : '';
        },
      },
      {
        field: 'onHand',
        headerName: 'Készleten',
        type: 'number',
      },
      {
        field: 'toBeOrdered',
        headerName: 'Rendelendő',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="toBeOrdered"
              type="number"
              variant="standard"
              fullWidth
              value={data.row.toBeOrdered}
              onChange={(e) => {
                const newValue = e.target.value ?? null;
                handleFieldChange(data.field, newValue, { ...data.row, [data.field]: newValue });
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });
              }}
            />
          );
        },
      },
      {
        field: 'fromStock',
        headerName: 'Raktárról',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="fromStock"
              type="number"
              variant="standard"
              fullWidth
              value={data.row.fromStock}
              onChange={(e) => {
                const newValue = e.target.value ?? null;
                handleFieldChange(data.field, newValue, { ...data.row, [data.field]: newValue });
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });
              }}
            />
          );
        },
      },
      {
        field: 'supplierOrderNum',
        headerName: 'Szállítói rendelés száma',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="supplierOrderNum"
              type="text"
              variant="standard"
              fullWidth
              value={data.row.supplierOrderNum}
              onChange={(e) => {
                const newValue = e.target.value ?? null;
                handleFieldChange(data.field, newValue, { ...data.row, [data.field]: newValue });
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });
              }}
            />
          );
        },
      },
    ]);
  };

  return (
    <>
      <Box sx={sxConfig.containerBox}>
        <Box sx={sxConfig.formBox}>
          <DatagridPremiumUi
            apiRef={apiRef}
            datagridIdentifier={DataGridIdentifiers.ProductionItemProposalTable}
            datagridInitialState={initialState}
            dataLoader={getList}
            refetch={{ refetchTable, setRefetchTable }}
            paginationAndfilterMode="server"
            height={840}
            columns={columns()}
            rows={reports.rows}
            rowCount={reports.rowCount}
            editMode="row"
            onCellDoubleClick={(_params, event) => {
              event.defaultMuiPrevented = true;
            }}
            onCellClick={(params, event) => {
              const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

              for (const rowId of rowsInEditMode) {
                if (params.row.productionItemId.toString() !== rowId) {
                  apiRef.current.stopRowEditMode({ id: rowId });
                }
              }
              const rowMode = apiRef.current.getRowMode(params.row.productionItemId);
              if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.productionItemId });
            }}
            getRowHeight={() => 'auto'}
            getRowId={(row) => row.productionItemId}
          />
        </Box>
      </Box>
    </>
  );
};

export default ProductionItemProposalTable;
